import clsx from 'clsx';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { SwipeDirections, useSwipeable } from 'react-swipeable';
import { ChevronLeft, ChevronRight } from '@insify/design-system/icons';
import { useBreakpoint } from '@insify/react-hooks';
import { Section } from '~components/Section';
import { Heading } from '~components/Heading';
import { Image } from '~components/Image';
import { Carousel, CarouselSlider } from '~components/Carousel';
import { flipClamp } from '~utils/number';
import { applyMarker } from '~utils/applyMarker';
import { getInitials } from '../utils';

import * as styles from './ComponentReviewsCards.module.css';

import type { ContentBlock } from '~components/types';
import type { CarouselConfig } from '~components/Carousel';
import type { ComponentReviewsProps, ContentfulComponentReviewItem } from '../types';

type NoteState = {
  currentIndex: number;
  nextIndex: number;
  isAnimating: boolean;
};

export const ComponentReviewsCards: ContentBlock<ComponentReviewsProps> = ({
  content: { heading, items, carouselInterval },
  ...sectionProps
}) => {
  const [noteState, setNoteState] = useState<NoteState>({
    currentIndex: 0,
    nextIndex: items.length === 1 ? 0 : 1,
    isAnimating: false,
  });

  const swipeDirection = useRef<SwipeDirections>('Left');

  const swipeCallback = ({ dir }: { dir: SwipeDirections }) => {
    if (dir === 'Left' || dir === 'Right') {
      swipeDirection.current = dir;
      setNoteState((state) => ({ ...state, isAnimating: true }));
    }
  };

  const swipeableHandlers = useSwipeable({
    onSwiped: swipeCallback,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const isDesktop = useBreakpoint('(min-width: 768px');

  const animationEndHandler = () => {
    setNoteState((state) => ({
      currentIndex: flipClamp(state.currentIndex + 1, 0, items.length - 1),
      nextIndex: flipClamp(state.nextIndex + 1, 0, items.length - 1),
      isAnimating: false,
    }));
  };

  const itemCount = items.length;

  const carouselConfig: CarouselConfig = useMemo(
    () => ({
      all: {
        slidesToShow: itemCount <= 3 ? 1 : 3,
        slideGap: '0px',
        autoplayInterval: carouselInterval,
        pauseAutoplayOnHover: true,
        autoplay: isDesktop,
      },
      '(min-width: 1060px)': {
        slidesToShow: itemCount <= 3 ? Math.min(itemCount, 2) : 3,
      },
      '(min-width: 1574px)': {
        slidesToShow: itemCount <= 4 ? Math.min(itemCount, 3) : 4,
      },
      '(min-width: 2088px)': {
        slidesToShow: itemCount <= 5 ? Math.min(itemCount, 4) : 5,
      },
      '(min-width: 2602px)': {
        slidesToShow: itemCount <= 6 ? Math.min(itemCount, 5) : 6,
      },
    }),
    [isDesktop],
  );

  return (
    <Section {...sectionProps} blockLayout='full-width' className={styles.section}>
      <Heading className={styles.heading} level={2}>
        {applyMarker(heading, { animated: true, markerColor: 'var(--green-50)' })}
      </Heading>
      <div className={styles.cardCarousel}>
        <div className={clsx(styles.stepperButtonWrapper, styles.left)}>
          <ChevronLeft className={styles.stepperButton} onClick={() => swipeCallback({ dir: 'Right' })} />
        </div>
        <div className={clsx(styles.cardStack, noteState.isAnimating && styles[`swipe${swipeDirection.current}`])}>
          <div className={clsx(styles.card, styles.bottom)} />
          <div className={clsx(styles.card, styles.middle)} />
          <div className={styles.cardGroup}>
            <ComponentReviewsCardsItem className={styles.next} content={items[noteState.nextIndex]} />
            <ComponentReviewsCardsItem
              className={styles.current}
              content={items[noteState.currentIndex]}
              {...swipeableHandlers}
              onAnimationEnd={animationEndHandler}
            />
          </div>
        </div>
        <div className={clsx(styles.stepperButtonWrapper, styles.right)}>
          <ChevronRight className={styles.stepperButton} onClick={() => swipeCallback({ dir: 'Left' })} />
        </div>
      </div>
      <Carousel config={carouselConfig}>
        <CarouselSlider
          className={styles.carouselSlider}
          style={
            {
              '--slidesToShowMobile': itemCount <= 3 ? 1 : 3,
              '--slidesToShow1060': itemCount <= 3 ? Math.min(itemCount, 2) : 3,
              '--slidesToShow1574': itemCount <= 4 ? Math.min(itemCount, 3) : 4,
              '--slidesToShow2088': itemCount <= 5 ? Math.min(itemCount, 4) : 5,
              '--slidesToShow2602': itemCount <= 6 ? Math.min(itemCount, 5) : 6,
              '--containerWidthMobile': itemCount <= 3 ? '100%' : '1542px',
              '--containerWidth1060': itemCount <= 3 ? `calc(514px * var(--slidesToShow1060))` : '1542px',
              '--containerWidth1574': itemCount <= 4 ? `calc(514px * var(--slidesToShow1574))` : '2056px',
              '--containerWidth2088': itemCount <= 5 ? `calc(514px * var(--slidesToShow2088))` : '2570px',
              '--containerWidth2602': itemCount <= 6 ? `calc(514px * var(--slidesToShow2602))` : '3084px',
            } as React.CSSProperties
          }
        >
          {items.map((item) => (
            <div className={styles.cardWrapper} key={item.content.content}>
              <ComponentReviewsCardsItem content={item} />
            </div>
          ))}
        </CarouselSlider>
      </Carousel>
    </Section>
  );
};

type ComponentReviewsCardsItemProps = Omit<JSX.IntrinsicElements['div'], 'content'> & {
  content: ContentfulComponentReviewItem;
};

export const ComponentReviewsCardsItem = forwardRef<HTMLDivElement, ComponentReviewsCardsItemProps>(
  ({ content: { content, author, authorPhoto, occupation }, ...props }, ref) => (
    <div {...props} className={clsx(styles.card, props.className)} ref={ref}>
      <blockquote className={styles.cardContent}>{content.content}</blockquote>
      <div className={styles.avatar}>
        {authorPhoto ? <Image className={styles.picture} image={authorPhoto} loading='eager' /> : getInitials(author)}
      </div>
      <div className={styles.author}>
        <span>{author}</span>
        <span className={styles.occupation}>{occupation}</span>
      </div>
    </div>
  ),
);
